import type { DietLog } from "@couples/shared";
import { useMemo } from "react";

export interface DietChartSeries {
  userId: string;
  name: string;
  color: string;
  /** 増減の基準。目標が未設定なら期間内でいちばん古い記録を使う */
  baseline: number;
}

interface Props {
  /** 古い順の日付(`YYYY-MM-DD`) */
  dates: string[];
  series: DietChartSeries[];
  logs: DietLog[];
}

const WIDTH = 320;
const HEIGHT = 136;
const PAD_L = 26;
const PAD_R = 6;
const PAD_T = 8;
const PAD_B = 16;

function xOf(index: number, count: number): number {
  if (count <= 1) return PAD_L;
  return PAD_L + (index / (count - 1)) * (WIDTH - PAD_L - PAD_R);
}

function shortDate(key: string): string {
  const [, month, day] = key.split("-");
  return month === undefined || day === undefined ? key : `${Number(month)}/${Number(day)}`;
}

/**
 * 二人の「開始からの増減」を同じ軸に重ねた折れ線。
 *
 * 体重の絶対値ではなく増減で描くのは、体格の違う二人を並べても
 * 「どちらが軽いか」の話にならないようにするため。目盛りは 1kg 刻みで、
 * 実データの幅に合わせて上下に少しだけ余白を足す。
 */
export default function DietTrendChart({ dates, series, logs }: Props) {
  const weightByKey = useMemo(() => {
    const map = new Map<string, number>();
    for (const log of logs) map.set(`${log.authorId}:${log.loggedOn}`, log.weight);
    return map;
  }, [logs]);

  const deltasOf = (item: DietChartSeries) =>
    dates.map((date) => {
      const weight = weightByKey.get(`${item.userId}:${date}`);
      return weight === undefined ? null : weight - item.baseline;
    });

  const allDeltas = series.flatMap((item) =>
    deltasOf(item).filter((delta): delta is number => delta !== null),
  );

  // 記録が1件も無い期間でも軸だけは描く。0 のまわりに ±1kg を取る
  const rawTop = allDeltas.length === 0 ? 1 : Math.max(...allDeltas);
  const rawBottom = allDeltas.length === 0 ? -1 : Math.min(...allDeltas);
  const top = Math.ceil(Math.max(rawTop, 0.2) * 2) / 2;
  const bottom = Math.floor(Math.min(rawBottom, -0.2) * 2) / 2;

  const yOf = (delta: number) =>
    PAD_T + ((top - delta) / (top - bottom)) * (HEIGHT - PAD_T - PAD_B);

  // 目盛りは 1kg 刻み。範囲が広いときは間引いて、線が潰れないようにする
  const step = Math.max(1, Math.ceil((top - bottom) / 5));
  const ticks: number[] = [];
  for (let value = Math.ceil(top / step) * step; value >= bottom; value -= step) {
    ticks.push(value);
  }

  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];

  return (
    <svg
      viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
      className="w-full"
      role="img"
      aria-label="開始からの増減の推移"
    >
      {ticks.map((delta) => (
        <g key={delta}>
          <line
            x1={PAD_L}
            x2={WIDTH - PAD_R}
            y1={yOf(delta)}
            y2={yOf(delta)}
            stroke="var(--color-line)"
            strokeWidth="1"
            strokeDasharray={delta === 0 ? undefined : "3 3"}
          />
          <text x="0" y={yOf(delta) + 3} fontSize="9" fill="var(--color-sub)">
            {delta === 0 ? "0" : delta > 0 ? `+${String(delta)}` : String(delta)}
          </text>
        </g>
      ))}

      {series.map((item) => {
        const deltas = deltasOf(item);
        const points = deltas
          .map((delta, index) =>
            delta === null ? null : `${String(xOf(index, dates.length))},${String(yOf(delta))}`,
          )
          .filter((point): point is string => point !== null)
          .join(" ");
        if (points === "") return null;

        // 最後の点だけ丸を置く。線の端で「今どこにいるか」が分かる
        let lastIndex = -1;
        deltas.forEach((delta, index) => {
          if (delta !== null) lastIndex = index;
        });
        const lastDelta = lastIndex >= 0 ? deltas[lastIndex] : undefined;

        return (
          <g key={item.userId}>
            <polyline
              points={points}
              fill="none"
              stroke={item.color}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {lastDelta !== undefined && lastDelta !== null && (
              <circle
                cx={xOf(lastIndex, dates.length)}
                cy={yOf(lastDelta)}
                r="3"
                fill={item.color}
              />
            )}
          </g>
        );
      })}

      {firstDate !== undefined && (
        <text x={PAD_L} y={HEIGHT - 3} fontSize="9" fill="var(--color-sub)">
          {shortDate(firstDate)}
        </text>
      )}
      {lastDate !== undefined && (
        <text
          x={WIDTH - PAD_R}
          y={HEIGHT - 3}
          fontSize="9"
          fill="var(--color-sub)"
          textAnchor="end"
        >
          {shortDate(lastDate)}
        </text>
      )}
    </svg>
  );
}
